import React, { useState, useEffect } from 'react';
import { Plus, Building2, Phone, Mail, MapPin, Edit2, Trash2, Eye, Search, Filter, ChevronRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  auto_repair: '🔧',
  medical: '🏥',
  dental: '🦷',
  legal: '⚖️',
  restaurant: '🍽️',
  salon: '💇',
  real_estate: '🏠',
  general: '🏢'
};

const categoryColors = {
  auto_repair: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  medical: 'bg-green-500/20 text-green-400 border-green-500/30',
  dental: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  legal: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  restaurant: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  salon: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  real_estate: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  general: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const Businesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  
  const [formData, setFormData] = useState({
    business_name: '',
    business_category: 'general',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    owner_phone_number: ''
  });

  useEffect(() => {
    fetchBusinesses();
    fetchCategories();
  }, []);

  const fetchBusinesses = async () => {
    try {
      const res = await axios.get(`${API}/businesses`);
      setBusinesses(res.data.businesses || []);
    } catch (err) {
      console.error('Failed to fetch businesses:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/business-categories`);
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const handleCreate = async () => {
    if (!formData.business_name || !formData.email) {
      toast.error('Business name and email are required');
      return;
    }

    try {
      await axios.post(`${API}/businesses`, formData);
      toast.success('Business created successfully!');
      setShowCreateModal(false);
      setFormData({
        business_name: '',
        business_category: 'general',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        owner_phone_number: ''
      });
      fetchBusinesses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create business');
    }
  };

  const handleUpdate = async () => {
    if (!selectedBusiness) return;

    try {
      await axios.put(`${API}/businesses/${selectedBusiness.id}`, formData);
      toast.success('Business updated!');
      setShowEditModal(false);
      fetchBusinesses();
    } catch (err) {
      toast.error('Failed to update business');
    }
  };

  const handleDelete = async (businessId) => {
    if (!window.confirm('Are you sure you want to delete this business?')) return;

    try {
      await axios.delete(`${API}/businesses/${businessId}`);
      toast.success('Business deleted');
      fetchBusinesses();
    } catch (err) {
      toast.error('Failed to delete business');
    }
  };

  const openEditModal = (business) => {
    setSelectedBusiness(business);
    setFormData({
      business_name: business.business_name || '',
      business_category: business.business_category || 'general',
      email: business.email || '',
      phone: business.phone || '',
      address: business.address || '',
      city: business.city || '',
      state: business.state || '',
      owner_phone_number: business.owner_phone_number || ''
    });
    setShowEditModal(true);
  };

  const filteredBusinesses = businesses.filter(b => {
    const matchesSearch = b.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          b.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || b.business_category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-background p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground flex items-center gap-3">
              <Building2 className="w-8 h-8 text-primary" />
              BUSINESSES
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Manage all business accounts
            </p>
          </div>
          
          <Button onClick={() => setShowCreateModal(true)} className="bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-2" /> Add Business
          </Button>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search businesses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-muted border-border"
            />
          </div>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 rounded-lg bg-muted border border-border text-foreground"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="card-modern p-4">
            <p className="text-muted-foreground text-xs">Total Businesses</p>
            <p className="text-2xl font-bold text-foreground">{businesses.length}</p>
          </Card>
          <Card className="card-modern p-4">
            <p className="text-muted-foreground text-xs">Active</p>
            <p className="text-2xl font-bold text-green-400">{businesses.filter(b => b.status === 'active').length}</p>
          </Card>
          <Card className="card-modern p-4">
            <p className="text-muted-foreground text-xs">Categories</p>
            <p className="text-2xl font-bold text-primary">{new Set(businesses.map(b => b.business_category)).size}</p>
          </Card>
          <Card className="card-modern p-4">
            <p className="text-muted-foreground text-xs">This Month</p>
            <p className="text-2xl font-bold text-foreground">
              {businesses.filter(b => {
                const created = new Date(b.created_at);
                const now = new Date();
                return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
              }).length}
            </p>
          </Card>
        </div>

        {/* Business List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        ) : filteredBusinesses.length === 0 ? (
          <Card className="card-modern p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <h3 className="text-xl font-bold text-foreground mb-2">No businesses yet</h3>
            <p className="text-muted-foreground mb-4">Add your first business to get started</p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" /> Add Business
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredBusinesses.map((business) => (
              <Card 
                key={business.id} 
                className="card-modern overflow-hidden hover:border-primary/50 transition-colors"
              >
                <div className="p-4">
                  {/* Category Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-2 py-1 rounded-full text-xs border ${categoryColors[business.business_category] || categoryColors.general}`}>
                      {categoryIcons[business.business_category] || '🏢'} {business.business_category?.replace('_', ' ')}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${business.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {business.status || 'active'}
                    </span>
                  </div>
                  
                  {/* Business Name */}
                  <h3 className="text-lg font-bold text-foreground mb-1">
                    {business.business_name}
                  </h3>
                  
                  {/* Pro Persona */}
                  <p className="text-sm text-primary mb-3">
                    Pro: {business.ai_persona_name || 'Alex'}
                  </p>
                  
                  {/* Contact Info */}
                  <div className="space-y-1 text-sm text-muted-foreground">
                    {business.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-3 h-3" />
                        <span className="truncate">{business.email}</span>
                      </div>
                    )}
                    {business.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-3 h-3" />
                        <span>{business.phone}</span>
                      </div>
                    )}
                    {(business.city || business.state) && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        <span>{[business.city, business.state].filter(Boolean).join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="border-t border-border px-4 py-3 bg-muted/20 flex items-center justify-between">
                  <div className="text-xs text-muted-foreground">
                    {business.subscription_tier === 'pro' ? 'Pro' : business.subscription_tier || 'Trial'}
                    {business.monthly_price > 0 && ` • $${business.monthly_price}/mo`}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEditModal(business)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(business.id)}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create Business Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Business</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1.5 block">Business Name *</label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData(f => ({ ...f, business_name: e.target.value }))}
                  placeholder="Acme Auto Repair"
                  className="bg-muted border-border"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-1.5 block">Category *</label>
                <select
                  value={formData.business_category}
                  onChange={(e) => setFormData(f => ({ ...f, business_category: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-muted border border-border text-foreground"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-1.5 block">Email *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                  placeholder="contact@business.com"
                  className="bg-muted border-border"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Phone</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+1 555-123-4567"
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Owner Phone (for SMS)</label>
                  <Input
                    value={formData.owner_phone_number}
                    onChange={(e) => setFormData(f => ({ ...f, owner_phone_number: e.target.value }))}
                    placeholder="+1 555-987-6543"
                    className="bg-muted border-border"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-1.5 block">Address</label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData(f => ({ ...f, address: e.target.value }))}
                  placeholder="123 Main Street"
                  className="bg-muted border-border"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">City</label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData(f => ({ ...f, city: e.target.value }))}
                    placeholder="Dayton"
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">State</label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData(f => ({ ...f, state: e.target.value }))}
                    placeholder="OH"
                    className="bg-muted border-border"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} className="bg-primary hover:bg-primary/90">
                Create Business
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Business Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Business</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1.5 block">Business Name</label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData(f => ({ ...f, business_name: e.target.value }))}
                  className="bg-muted border-border"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-1.5 block">Category</label>
                <select
                  value={formData.business_category}
                  onChange={(e) => setFormData(f => ({ ...f, business_category: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-muted border border-border text-foreground"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-1.5 block">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                  className="bg-muted border-border"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Phone</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))}
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Owner Phone</label>
                  <Input
                    value={formData.owner_phone_number}
                    onChange={(e) => setFormData(f => ({ ...f, owner_phone_number: e.target.value }))}
                    className="bg-muted border-border"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-1.5 block">Address</label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData(f => ({ ...f, address: e.target.value }))}
                  className="bg-muted border-border"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">City</label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData(f => ({ ...f, city: e.target.value }))}
                    className="bg-muted border-border"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">State</label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData(f => ({ ...f, state: e.target.value }))}
                    className="bg-muted border-border"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} className="bg-primary hover:bg-primary/90">
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Businesses;
